import { useEffect, useState } from "react";
import { CalendarClock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";

export const BookCall = () => {
  const { lang, t } = useLang();
  const b = t.booking;
  const [slots, setSlots] = useState([]);
  const [day, setDay] = useState("");
  const [slot, setSlot] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [vacation, setVacation] = useState(null);

  useEffect(() => {
    api.get("/appointments/slots").then((r) => {
      setSlots(r.data.slots);
      if (r.data.slots[0]) setDay(r.data.slots[0].slice(0, 10));
    }).catch(() => {});
    api.get("/vacation").then((r) => setVacation(r.data.active ? r.data.until : null)).catch(() => {});
  }, []);

  const days = [...new Set(slots.map((s) => s.slice(0, 10)))].slice(0, 6);
  const daySlots = slots.filter((s) => s.startsWith(day));
  const fmtDay = (d) =>
    new Date(`${d}T00:00:00`).toLocaleDateString(lang === "fr" ? "fr-BE" : "en-GB", { weekday: "short", day: "numeric", month: "short" });

  const book = async () => {
    if (!slot || name.trim().length < 2 || phone.trim().length < 8) {
      toast.error(b.invalid);
      return;
    }
    setBusy(true);
    try {
      await api.post("/appointments", { name, phone, email, slot, lang });
      setDone(slot);
    } catch (err) {
      toast.error(err.response?.status === 409 ? b.taken : b.failed);
      api.get("/appointments/slots").then((r) => setSlots(r.data.slots)).catch(() => {});
    } finally {
      setBusy(false);
    }
  };

  if (!slots.length && !vacation) return null;

  const pill = (active) =>
    `px-3 py-1.5 text-xs font-semibold rounded-full border transition-colors duration-300 ${
      active ? "border-[#FF7900] bg-[#FF7900]/15 text-[#FF7900]" : "border-white/15 text-white/50 hover:border-white/40"
    }`;

  return (
    <div className="mt-10 border border-white/10 bg-[#111111] p-6" data-testid="book-call-widget">
      <p className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#FF7900] mb-2">
        <CalendarClock className="w-4 h-4" /> {b.title}
      </p>
      {vacation && (
        <p className="mb-4 border border-amber-500/40 bg-amber-500/10 text-amber-400 text-xs px-3 py-2.5" data-testid="book-vacation-notice">
          {b.vacation.replace("{d}", new Date(`${vacation}T00:00:00`).toLocaleDateString(lang === "fr" ? "fr-BE" : "en-GB", { day: "numeric", month: "long" }))}
        </p>
      )}
      {done ? (
        <div className="flex items-start gap-3 py-3" data-testid="book-call-success">
          <CheckCircle2 className="w-6 h-6 text-emerald-400 shrink-0" />
          <p className="text-sm text-white/70">
            {b.success}{" "}
            <span className="text-emerald-400 font-semibold">
              {new Date(done).toLocaleString(lang === "fr" ? "fr-BE" : "en-GB", { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" })}
            </span>
          </p>
        </div>
      ) : (
        <>
          <p className="text-sm text-white/50 mb-4">{b.subtitle}</p>
          <div className="flex flex-wrap gap-2 mb-3">
            {days.map((d) => (
              <button key={d} onClick={() => { setDay(d); setSlot(""); }} className={pill(day === d)} data-testid={`book-day-${d}`}>
                {fmtDay(d)}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 mb-4" data-testid="book-slots">
            {daySlots.map((s) => (
              <button key={s} onClick={() => setSlot(s)} className={pill(slot === s)} data-testid={`book-slot-${s.slice(11)}`}>
                {s.slice(11)}
              </button>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 gap-3 mb-4">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={b.name}
              data-testid="book-name-input"
              className="bg-black/40 border border-white/15 px-4 py-2.5 text-sm outline-none focus:border-[#FF7900] transition-colors duration-300"
            />
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder={b.phone}
              data-testid="book-phone-input"
              className="bg-black/40 border border-white/15 px-4 py-2.5 text-sm outline-none focus:border-[#FF7900] transition-colors duration-300"
            />
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={b.email}
              type="email"
              data-testid="book-email-input"
              className="sm:col-span-2 bg-black/40 border border-white/15 px-4 py-2.5 text-sm outline-none focus:border-[#FF7900] transition-colors duration-300"
            />
          </div>
          <button
            onClick={book}
            disabled={busy || !slot}
            data-testid="book-submit-button"
            className="inline-flex items-center gap-2 bg-[#FF7900] hover:bg-white hover:text-black disabled:opacity-40 text-white text-sm font-semibold px-6 py-3 rounded-full transition-colors duration-300"
          >
            {busy ? b.booking : b.cta}
          </button>
        </>
      )}
    </div>
  );
};
